export class TaxonomicStorage {
    static STORAGE_KEY_PREFIX = 'taxonomic_progress_';

    /**
     * Save game state to localStorage
     */
    static saveState(game) {
        const key = this.STORAGE_KEY_PREFIX + game.state.puzzleDate;

        // Don't save miniGame instance - it can't be serialized properly
        let deepDiveContextToSave = null;
        if (game.state.deepDiveContext) {
            const { miniGame, ...contextWithoutMiniGame } = game.state.deepDiveContext;
            deepDiveContextToSave = contextWithoutMiniGame;
        }

        const stateToSave = {
            version: 6, // Version 6: Add completion tracking (completionStatus, completionTime, completedAt, timerState)
            puzzleDate: game.state.puzzleDate,
            gridSize: game.getVisualGridSize(), // Visual grid size (square root of total words)
            mode: game.state.mode,
            selectedWords: game.state.selectedWords,
            pairedWords: game.state.pairedWords,
            completedCategories: game.state.completedCategories,
            deepDiveContext: deepDiveContextToSave,
            shuffledWords: game.state.shuffledWords,
            mainBoardState: game.state.mainBoardState,
            stackedPairs: game.state.stackedPairs,
            completionStatus: game.state.completionStatus || 'in_progress',
            completionTime: game.state.completionTime || null,
            completedAt: game.state.completedAt || null,
            timerState: game.state.timerState || { accumulatedTime: 0 },
            timestamp: Date.now()
        };

        try {
            localStorage.setItem(key, JSON.stringify(stateToSave));
            return true;
        } catch (error) {
            // If quota exceeded, try cleaning up old states and retry
            if (error.name === 'QuotaExceededError' || error.code === 22) {
                console.warn('Storage quota exceeded, cleaning up old states...');
                this.clearOldStates();
                try {
                    localStorage.setItem(key, JSON.stringify(stateToSave));
                    console.info('Save succeeded after cleanup');
                    return true;
                } catch (retryError) {
                    console.error('Still unable to save after cleanup:', retryError);
                    return false;
                }
            }
            console.warn('Failed to save to localStorage:', error);
            return false;
        }
    }

    /**
     * Load game state from localStorage
     */
    static loadState(puzzleDate, expectedGridSize) {
        const key = this.STORAGE_KEY_PREFIX + puzzleDate;

        try {
            const saved = localStorage.getItem(key);
            if (!saved) return null;

            const state = JSON.parse(saved);

            // Verify it's for the correct date
            if (state.puzzleDate !== puzzleDate) {
                return null;
            }

            // Check version compatibility
            // Version 6: Add completion tracking (completionStatus, completionTime, completedAt, timerState)
            // Version 5: Add stackedPairs
            // Version 4: Don't save miniGame
            // Version 3: Save mainBoardState
            if (state.version < 3 || state.version > 6) {
                console.warn(`Incompatible save state version ${state.version}, discarding`);
                localStorage.removeItem(key);
                return null;
            }

            // Migrate v3/v4 to v5 by adding empty stackedPairs
            if (!state.stackedPairs) {
                state.stackedPairs = {};
            }

            // Ensure deepDiveContext has required fields if present
            if (state.deepDiveContext) {
                if (!state.deepDiveContext.pairedIndices) {
                    state.deepDiveContext.pairedIndices = [];
                }
            }

            // Migrate v5 to v6 by adding completion tracking fields
            if (state.version === 5) {
                // Determine if game was completed
                const totalCategories = state.mainBoardState?.totalCategories ||
                    (state.completedCategories?.length > 0 ? state.completedCategories.length : null);
                const isCompleted = totalCategories !== null &&
                    state.completedCategories?.length === totalCategories;

                state.completionStatus = isCompleted ? 'completed' : 'in_progress';
                state.completionTime = null; // Unknown for migrated states
                state.completedAt = isCompleted ? Date.now() : null;
                state.timerState = { accumulatedTime: 0 };
                state.version = 6;
            }

            // Ensure v6 fields exist (for any state that may have been corrupted)
            if (!state.completionStatus) {
                state.completionStatus = 'in_progress';
            }
            if (state.completionTime === undefined) {
                state.completionTime = null;
            }
            if (state.completedAt === undefined) {
                state.completedAt = null;
            }
            if (!state.timerState) {
                state.timerState = { accumulatedTime: 0 };
            }

            // Verify grid size matches current puzzle
            if (expectedGridSize && state.gridSize !== expectedGridSize) {
                console.warn(`Grid size mismatch: saved=${state.gridSize}, expected=${expectedGridSize}`);
                localStorage.removeItem(key);
                return null;
            }

            return state;
        } catch (error) {
            console.warn('Failed to load from localStorage:', error);
            return null;
        }
    }

    /**
     * Clear old puzzle states
     */
    static clearOldStates() {
        const keys = Object.keys(localStorage);
        const taxonomicKeys = keys.filter(k => k.startsWith(this.STORAGE_KEY_PREFIX));

        taxonomicKeys.forEach(key => {
            try {
                const saved = localStorage.getItem(key);
                const state = JSON.parse(saved);
                const age = Date.now() - state.timestamp;

                // Remove states older than 7 days
                if (age > 7 * 24 * 60 * 60 * 1000) {
                    localStorage.removeItem(key);
                }
            } catch (error) {
                // If we can't parse it, remove it
                localStorage.removeItem(key);
            }
        });
    }

    /**
     * Mark a puzzle as completed with the given time.
     * @param {string} dateStr - The puzzle date (YYYY-MM-DD format)
     * @param {number} completionTime - Time taken to complete in milliseconds
     * @returns {boolean} True if save succeeded, false otherwise
     */
    static markCompleted(dateStr, completionTime) {
        const state = this.loadState(dateStr);
        if (!state) return false;

        state.completionStatus = 'completed';
        state.completionTime = completionTime;
        state.completedAt = Date.now();
        state.timestamp = Date.now();

        const key = this.STORAGE_KEY_PREFIX + dateStr;

        try {
            localStorage.setItem(key, JSON.stringify(state));
            return true;
        } catch (error) {
            if (error.name === 'QuotaExceededError' || error.code === 22) {
                console.warn('Storage quota exceeded, cleaning up old states...');
                this.clearOldStates();
                try {
                    localStorage.setItem(key, JSON.stringify(state));
                    return true;
                } catch (retryError) {
                    console.error('Still unable to mark completed after cleanup:', retryError);
                    return false;
                }
            }
            console.warn('Failed to mark completed:', error);
            return false;
        }
    }

    /**
     * Get completion data for a specific date.
     */
    static getCompletionData(dateStr) {
        const state = this.loadState(dateStr);
        if (!state) return null;
        return {
            completed: state.completionStatus === 'completed',
            completionTime: state.completionTime,
            completedAt: state.completedAt
        };
    }

    /**
     * Get completion data for all saved puzzles.
     */
    static getAllCompletionData() {
        const data = {};
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key?.startsWith(this.STORAGE_KEY_PREFIX)) {
                const dateStr = key.replace(this.STORAGE_KEY_PREFIX, '');
                const state = this.loadState(dateStr);
                if (state) {
                    const isCompleted = state.completionStatus === 'completed';
                    data[dateStr] = {
                        completed: isCompleted,
                        inProgress: !isCompleted,
                        completionTime: state.completionTime
                    };
                }
            }
        }
        return data;
    }
}
